import { useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { supabase } from '@/lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Refactor Fase 9: callback para OAuth de Supabase Auth (Google sign-in).
 *
 * Supabase detecta automaticamente los tokens del hash de la URL
 * (#access_token=...&refresh_token=...) gracias a detectSessionInUrl: true.
 * Este componente solo espera a que la sesion este lista y redirige a /dashboard.
 *
 * El bootstrap + onAuthStateChange de App.js se encarga de persistir el token
 * en localStorage y setear el user, asi que aca solo necesitamos:
 *   1. Esperar a que getSession() devuelva la sesion lista
 *   2. Fetch /api/auth/me para tener el user completo en el contexto
 *   3. Navegar a /dashboard
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Esperar a que Supabase procese el hash y construya la sesion.
        // Hacemos polling corto porque detectSessionInUrl es async.
        let session = null;
        for (let i = 0; i < 20; i++) {
          const { data } = await supabase.auth.getSession();
          if (data?.session?.access_token) {
            session = data.session;
            break;
          }
          await new Promise((r) => setTimeout(r, 150));
        }

        if (!session) {
          navigate('/login', {
            replace: true,
            state: { error: 'No se pudo iniciar sesion. Intenta de nuevo.' },
          });
          return;
        }

        const accessToken = session.access_token;

        // Fetch user desde el backend (devuelve User completo con plan, company, etc.)
        try {
          const meRes = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          login(accessToken, meRes.data);
        } catch (err) {
          console.warn('GET /api/auth/me fallo, usando metadata de Supabase:', err);
          // Fallback con metadata de Supabase Auth
          login(accessToken, {
            id: session.user?.id,
            email: session.user?.email,
            name: session.user?.user_metadata?.name || session.user?.email?.split('@')[0],
            plan: 'free',
          });
        }

        // Limpiar el hash de la URL para que no quede colgado
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/dashboard', { replace: true });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', {
          replace: true,
          state: { error: 'Error al iniciar sesion. Intenta de nuevo.' },
        });
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-ai-glow border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-white text-lg">Iniciando sesion con Google...</p>
        <p className="text-secondary text-sm mt-2">Verificando credenciales...</p>
      </div>
    </div>
  );
}
